import type { Texture } from "pixi.js";
import type { Log } from "@slot/presentation";
import type { BootLaunchPayload, BrandedAssetResolver } from "@slot/game-shell";
import type { CoinJackpotTier, JackpotTier, PotKind } from "../rules/symbols";
import type { BlurredSymbol } from "../symbols/SymbolCatalog";

/**
 * The symbols' art: the blank (the empty cell's plaque, rolling with the coins), the plain coin (its value drawn by the
 * game), the three jackpot coins, the mystery, the collect.
 */
export interface SymbolTextures {
  readonly blank: Texture;
  /** An active line's empty cell (decision 56): the red badge (missing, the blue one). */
  readonly blankLit: Texture | null;
  /** The fillers a rolling cell shows, blurred along the roll (missing, the sharp art rolls instead). */
  readonly blurred: Readonly<Record<BlurredSymbol, Texture | null>>;
  readonly coin: Texture;
  readonly jackpot: Readonly<Record<CoinJackpotTier, Texture>>;
  readonly mystery: Texture;
  /** The designer's mystery jackpot: its window, "MYSTERY JACKPOT" over it. */
  readonly mysteryJackpot: Texture;
  readonly collect: Texture;
}

/**
 * Everything the round's visuals need, resolved ONCE from the loaded bundles through the branded resolver. The symbols
 * are required (`getAssetOrThrow`: a missing one fails the boot, the boundary shows its popup); missing optional art
 * resolves to null and is logged, so the game still boots and the view draws what it has.
 */
export interface GameVisualAssets {
  readonly symbols: SymbolTextures;
  readonly textures: {
    /** The grid's frame (three rows, sliced to grow) and the middle row's own frame over it. */
    readonly frame: Texture | null;
    readonly rowFrame: Texture | null;
    /** A cell's background: blue, and red for the tease and under the coins that start the bonus. */
    readonly cellBlue: Texture | null;
    readonly cellRed: Texture | null;
    readonly pots: Readonly<Record<PotKind, Texture | null>>;
    /** Each pot's name, over its base. */
    readonly potLabels: Readonly<Record<PotKind, Texture | null>>;
    /** The heap of coins the pots stand in. */
    readonly coinsHeap: Texture | null;
    /** A pot's coin pile at 1, 2 and 3 gems. */
    readonly piles: readonly (Texture | null)[];
    readonly gems: Readonly<Record<PotKind, Texture | null>>;
    readonly plaques: Readonly<Record<JackpotTier, Texture | null>>;
    /** The LINES selector (decision 56): its frame with the tab and the window, and an arrow pointing right. */
    readonly linesPanel: Texture | null;
    readonly linesArrow: Texture | null;
    /** The respin meter: its frame, a lit slot, the purple an unlit slot stretches. */
    readonly meterFrame: Texture | null;
    readonly meterSlotOn: Texture | null;
    readonly meterSlotOff: Texture | null;
    readonly multiplierBadge: Texture | null;
    readonly glow: Texture | null;
    readonly sparkle: Texture | null;
    readonly card: Texture | null;
  };
}

export function loadGameVisualAssets(input: { payload: BootLaunchPayload; log: Log }): GameVisualAssets {
  const branded: BrandedAssetResolver = input.payload.brandedAssets;
  const optional = (path: string): Texture | null => {
    const asset = branded.getAssetOrNull<Texture>(path);
    if (asset === null) input.log(`[GameVisualAssets] texture "${path}" not loaded`);
    return asset;
  };
  const required = (path: string): Texture => branded.getAssetOrThrow<Texture>(path);
  return {
    symbols: {
      blank: required("symbols/blank"),
      blankLit: optional("symbols/blank_lit"),
      blurred: {
        blank: optional("symbols/blank_blur"),
        blankLit: optional("symbols/blank_lit_blur"),
        coin: optional("symbols/coin_blur"),
        mini: optional("symbols/coin_mini_blur"),
        minor: optional("symbols/coin_minor_blur"),
        mystery: optional("symbols/coin_mystery_blur"),
      },
      coin: required("symbols/coin"),
      jackpot: { mini: required("symbols/coin_mini"), minor: required("symbols/coin_minor"), major: required("symbols/coin_major") },
      mystery: required("symbols/coin_mystery"),
      mysteryJackpot: required("symbols/mystery_jackpot"),
      collect: required("symbols/coin_collect"),
    },
    textures: {
      frame: optional("grid/frame"),
      rowFrame: optional("grid/row_frame"),
      cellBlue: optional("grid/cell_blue"),
      cellRed: optional("grid/cell_red"),
      pots: { life: optional("pots/pot_life"), multi: optional("pots/pot_multi"), grow: optional("pots/pot_grow") },
      potLabels: { life: optional("pots/label_life"), multi: optional("pots/label_multi"), grow: optional("pots/label_grow") },
      coinsHeap: optional("pots/coins_heap"),
      piles: [optional("pots/pile_1"), optional("pots/pile_2"), optional("pots/pile_3")],
      gems: { life: optional("pots/gem_life"), multi: optional("pots/gem_multi"), grow: optional("pots/gem_grow") },
      plaques: {
        mini: optional("jackpots/plaque_mini"),
        minor: optional("jackpots/plaque_minor"),
        major: optional("jackpots/plaque_major"),
        grand: optional("jackpots/plaque_grand"),
        royal: optional("jackpots/plaque_royal"),
      },
      linesPanel: optional("lines/panel"),
      linesArrow: optional("lines/arrow"),
      meterFrame: optional("meter/frame"),
      meterSlotOn: optional("meter/slot_on"),
      meterSlotOff: optional("meter/slot_off"),
      multiplierBadge: optional("effects/multiplier_badge"),
      glow: optional("effects/glow"),
      sparkle: optional("effects/sparkle"),
      card: optional("panels/card"),
    },
  };
}
